import type {CollaborationParticipant, PresenceMessage, PresencePayload, RemoteSession, RemoteSessionsById} from '@PosterWhiteboard/collaboration/poster-presence.types';
import {MAX_PRESENCE_SELECTED_ITEM_IDS, PRESENCE_COLORS, PRESENCE_ENTITY_ID_PATTERN, PresenceMessageType} from '@PosterWhiteboard/collaboration/poster-presence.types';

/**
 * Returns the sessions with one tab's latest publication folded in; `sessions` is never mutated.
 * A session stays with the person it was first seen from: a publication under a known session id
 * by someone else is ignored, so no tab can take over or evict another's presence.
 */
export const upsertRemoteSession = (sessions: RemoteSessionsById, payload: PresencePayload, participant: CollaborationParticipant, now: number): RemoteSessionsById => {
  const existing = Object.hasOwn(sessions, payload.sessionId) ? sessions[payload.sessionId] : undefined;
  if (existing !== undefined && existing.participant.userId !== participant.userId) {
    return sessions;
  }
  const session: RemoteSession = {
    sessionId: payload.sessionId,
    pageId: payload.pageId,
    cursor: payload.cursor,
    selectedItemIds: payload.selectedItemIds,
    participant,
    color: existing?.color ?? colorForSession(payload.sessionId),
    lastSeenAt: now,
  };
  return {...sessions, [payload.sessionId]: session};
};

/** A leave is honoured only from the person the session belongs to. */
export const removeRemoteSession = (sessions: RemoteSessionsById, sessionId: string, participant: CollaborationParticipant | null = null): RemoteSessionsById => {
  if (!Object.hasOwn(sessions, sessionId) || (participant !== null && sessions[sessionId].participant.userId !== participant.userId)) {
    return sessions;
  }
  const {[sessionId]: _removed, ...remaining} = sessions;
  return remaining;
};

/** Drops sessions that stopped publishing: a tab that lost its connection without a leave event. */
export const pruneStaleSessions = (sessions: RemoteSessionsById, now: number, staleAfterMs: number): RemoteSessionsById => {
  const liveEntries = Object.entries(sessions).filter(([, session]) => now - session.lastSeenAt < staleAfterMs);
  return liveEntries.length === Object.keys(sessions).length ? sessions : Object.fromEntries(liveEntries);
};

/** A stable colour per session id, so a cursor keeps its colour across publications and across viewers. */
export const colorForSession = (sessionId: string): string => {
  let hash = 0;
  for (const character of sessionId) {
    hash = (hash * 31 + character.charCodeAt(0)) % PRESENCE_COLORS.length;
  }
  return PRESENCE_COLORS[hash];
};

export const isPresenceMessage = (value: unknown): value is PresenceMessage => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  if (!isPresenceEntityId(candidate.sessionId)) {
    return false;
  }
  if (candidate.type === PresenceMessageType.LEAVE) {
    return true;
  }
  return candidate.type === PresenceMessageType.UPDATE && isPresencePayloadShape(candidate);
};

const isPresencePayloadShape = (candidate: Record<string, unknown>): boolean => {
  const hasPageId = candidate.pageId === null || isPresenceEntityId(candidate.pageId);
  const hasCursor = candidate.cursor === null || isCanvasPointShape(candidate.cursor);
  const hasSelection = Array.isArray(candidate.selectedItemIds) && candidate.selectedItemIds.length <= MAX_PRESENCE_SELECTED_ITEM_IDS && candidate.selectedItemIds.every(isPresenceEntityId);
  return hasPageId && hasCursor && hasSelection;
};

/** An id a peer sent is used as a key into plain objects, so a prototype property name is refused along with any other odd string. */
const isPresenceEntityId = (value: unknown): value is string => {
  return typeof value === 'string' && PRESENCE_ENTITY_ID_PATTERN.test(value) && !Object.hasOwn(Object.prototype, value);
};

const isCanvasPointShape = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return typeof candidate.x === 'number' && typeof candidate.y === 'number';
};

export const isCollaborationParticipant = (value: unknown): value is CollaborationParticipant => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return typeof candidate.userId === 'number' && typeof candidate.name === 'string' && typeof candidate.avatarUrl === 'string';
};
